import {
  exceedsPhotoPixelBudget,
  MAX_PHOTO_EDGE_PX,
  MAX_PRODUCT_MEDIA_SIZE_BYTES,
  PHOTO_NORMALIZATION_MIN_BYTES,
} from "@shared/products/photo-constraints";
import { encodePhotoForStorage, extensionFor } from "./encode-photo";
import { PhotoValidationError } from "./validation";

const MAX_UPLOAD_EDGE = MAX_PHOTO_EDGE_PX;

function photoNormalizationError(): PhotoValidationError {
  return new PhotoValidationError(
    "This image could not be optimized. Choose a smaller image.",
  );
}

function needsDownscale(file: File, width: number, height: number): boolean {
  return (
    Math.max(width, height) > MAX_UPLOAD_EDGE ||
    file.size > PHOTO_NORMALIZATION_MIN_BYTES
  );
}

function originalIsStorable(file: File, exceedsPixelBudget: boolean): boolean {
  return !exceedsPixelBudget && file.size <= MAX_PRODUCT_MEDIA_SIZE_BYTES;
}

async function encodeDownscaled(
  file: File,
  bitmap: ImageBitmap,
  exceedsPixelBudget: boolean,
): Promise<File> {
  const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(bitmap.width, bitmap.height));
  const targetWidth = Math.round(bitmap.width * scale);
  const targetHeight = Math.round(bitmap.height * scale);
  const blob = await encodePhotoForStorage(bitmap, targetWidth, targetHeight);
  const keepOriginal = originalIsStorable(file, exceedsPixelBudget);
  if (!blob) {
    if (keepOriginal) return file;
    throw photoNormalizationError();
  }
  const overEdgeCap = Math.max(bitmap.width, bitmap.height) > MAX_UPLOAD_EDGE;
  if (blob.size >= file.size && !overEdgeCap && keepOriginal) return file;
  const mime = blob.type || "image/webp";
  const newName = file.name.replace(/\.[^./\\]+$/, "") + "." + extensionFor(mime);
  return new File([blob], newName, { type: mime });
}

export async function downscaleImageIfNeeded(file: File): Promise<File> {
  if (
    file.type === "image/gif" ||
    typeof createImageBitmap !== "function" ||
    typeof document === "undefined"
  ) {
    return file;
  }

  let bitmap: ImageBitmap | null = null;
  let exceedsPixelBudget = false;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    exceedsPixelBudget = exceedsPhotoPixelBudget(bitmap.width, bitmap.height);
    if (!needsDownscale(file, bitmap.width, bitmap.height)) {
      return file;
    }
    return await encodeDownscaled(file, bitmap, exceedsPixelBudget);
  } catch (error) {
    if (error instanceof PhotoValidationError) throw error;
    if (originalIsStorable(file, exceedsPixelBudget)) return file;
    throw photoNormalizationError();
  } finally {
    bitmap?.close();
  }
}
